var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/components/DragToPan.tsx
import {
  Draggable,
  SpecialModifierKey,
  useModifierKey
} from "@ballpark/design-core";
import { PointUtils as PointUtils4 } from "@ballpark/design-creator";
import { useTransactionCallback } from "@ballpark/design-plugin-react";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState as useState2 } from "react";

// src/react.tsx
import { usePropReaction } from "@ballpark/core";
import { useDesign } from "@ballpark/design-plugin-react";
import { createContext, useContext, useState } from "react";
import { useMemoOne } from "use-memo-one";

// src/View.ts
import {
  MatrixUtils as MatrixUtils3,
  NumberUtils as NumberUtils2,
  PointUtils as PointUtils3,
  SizeUtils
} from "@ballpark/design-creator";
import { computed, makeAutoObservable } from "mobx";

// src/actions.ts
import {
  MatrixUtils,
  NumberUtils,
  PointUtils,
  RectangleUtils,
  selectors as designSelectors
} from "@ballpark/design-creator";
import { findLastIndex } from "lodash-es";

// src/common.ts
var ZOOM_FACTORS = [0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 1, 2, 4, 8, 16];

// src/actions.ts
var MAX_ZOOM = 8;
var MIN_ZOOM = 0.01;
var zoomLevelMin = 0;
var zoomLevelMax = ZOOM_FACTORS.length - 1;
function zoomLevelToFactor(zoomLevel2) {
  return ZOOM_FACTORS[Math.max(Math.min(zoomLevelMax, zoomLevel2), zoomLevelMin)];
}
var pan = (view, offset2, options = {}) => {
  const { keepFollowing = false } = options;
  if (!keepFollowing)
    view.following = null;
  view.matrix = MatrixUtils.translate(
    view.rawMatrix,
    PointUtils.divide(offset2, view.zoomFactor)
  );
};
var panToPoint = (view, point, options = {}) => {
  const offset2 = PointUtils.multiply(
    PointUtils.subtract(view.rawCenter, point),
    view.zoomFactor
  );
  pan(view, offset2, options);
};
var panToArtboardCenter = (view, design) => {
  const artboardCenter = designSelectors.getArtboardCenter(design);
  panToPoint(view, artboardCenter);
};
var panAndZoomToFitArtboard = (view, design) => {
  panToArtboardCenter(view, design);
  zoomToFitArtboard(view, design);
};
var panToCluster = (view, design) => {
  const { largestCluster: largestCluster2 } = view;
  if (!largestCluster2)
    return;
  const clusterCenter = RectangleUtils.getCenter(largestCluster2);
  panToPoint(view, clusterCenter);
};
var panAndZoomToFitCluster = (view, design, options = { constrainToZoomScale: true }) => {
  const { largestCluster: largestCluster2 } = view;
  if (!largestCluster2)
    return;
  const clusterCenter = RectangleUtils.getCenter(largestCluster2);
  panToPoint(view, clusterCenter);
  zoomToFitBounds(view, largestCluster2, options);
};
var zoomToFitBounds = (view, bounds2, options = { constrainToZoomScale: true }) => {
  const viewSize = options.padding ? RectangleUtils.expand(
    { point: [0, 0], size: view.size },
    -2 * options.padding
  ).size : view.size;
  const [targetWidth, targetHeight] = bounds2.size;
  const [viewWidth, viewHeight] = viewSize;
  let zoomFactor2;
  if (!options.constrainToZoomScale) {
    zoomFactor2 = Math.min(viewWidth / targetWidth, viewHeight / targetHeight);
    if (zoomFactor2 > 1)
      zoomFactor2 = 1;
  } else {
    const fitZoomLevel = findLastIndex(ZOOM_FACTORS, (level) => {
      if (level > 1)
        return false;
      return targetWidth * level <= viewWidth && targetHeight * level <= viewHeight;
    });
    const zoomLevel2 = fitZoomLevel !== -1 ? fitZoomLevel : 0;
    zoomFactor2 = zoomLevelToFactor(zoomLevel2);
  }
  setZoomFactor(view, zoomFactor2, {
    center: view.rawCenter
  });
};
var zoomToFitArtboard = (view, design) => {
  const artboardBounds = designSelectors.getArtboardBounds(design);
  zoomToFitBounds(view, artboardBounds);
};
var zoomToFitCluster = (view, design) => {
  const { largestCluster: largestCluster2 } = view;
  if (!largestCluster2)
    return;
  zoomToFitBounds(view, largestCluster2);
};
var setZoomFactor = (view, newZoomFactor, options = {}) => {
  const { keepFollowing, center: center2 = view.rawCenter } = options;
  const cappedZoomFactor = Math.min(
    Math.max(MIN_ZOOM, newZoomFactor),
    MAX_ZOOM
  );
  if (NumberUtils.isCloseTo(view.zoomFactor, newZoomFactor))
    return;
  const zoomDelta = cappedZoomFactor / view.zoomFactor;
  if (!keepFollowing)
    view.following = null;
  view.matrix = MatrixUtils.scale(
    view.rawMatrix,
    [zoomDelta, zoomDelta],
    center2
  );
};
var zoomIn = (view, options = {}) => {
  const zoomFactor2 = zoomLevelToFactor(view.zoomLevel + 1);
  setZoomFactor(view, zoomFactor2, options);
};
var zoomOut = (view, options = {}) => {
  const zoomFactor2 = zoomLevelToFactor(view.zoomLevel - 1);
  setZoomFactor(view, zoomFactor2, options);
};

// src/selectors/cluster.ts
import {
  PointUtils as PointUtils2,
  RectangleUtils as RectangleUtils2,
  selectors
} from "@ballpark/design-creator";
import { groupBy, maxBy } from "lodash-es";
import { action } from "mobx";

// src/utils/optics.ts
var OpticsPoint = class {
  constructor(index, datum, vector) {
    this.reachability = -1;
    this.coreDistance = null;
    this.isProcessed = false;
    this.index = index;
    this.datum = datum;
    this.vector = vector;
  }
};
var Heap = class {
  constructor() {
    this.heap = [];
    this.size = 0;
  }
  swap(i1, i2) {
    const temp = this.heap[i1];
    this.heap[i1] = this.heap[i2];
    this.heap[i2] = temp;
  }
  insert(d) {
    this.heap[this.size] = d;
    this.size++;
    this.bubbleUp(this.size - 1);
  }
  isEmpty() {
    return this.size === 0;
  }
  bubbleUp(idx) {
    if (!idx)
      return;
    const parentIndex = Math.floor(idx / 2);
    const parent = this.heap[parentIndex];
    const point = this.heap[idx];
    if (parent && point && parent.reachability >= point.reachability) {
      this.swap(parentIndex, idx);
      this.bubbleUp(parentIndex);
    }
  }
  get() {
    const result = this.heap[0];
    this.heap[0] = this.heap[this.size - 1];
    this.heap[this.size - 1] = null;
    this.size--;
    this.bubbleDown(0);
    return result;
  }
  bubbleDown(idx) {
    const left = this.heap[idx * 2];
    const right = this.heap[idx * 2 + 1];
    let better;
    let betterIndex;
    if (!left && !right)
      return;
    better = left;
    betterIndex = idx * 2;
    if (right && better && better.reachability > right.reachability) {
      better = right;
      betterIndex = idx * 2 + 1;
    }
    if (better && better.reachability < this.heap[idx].reachability) {
      this.swap(betterIndex, idx);
      this.bubbleDown(betterIndex);
    }
  }
  update(d) {
    for (let i = 0; i < this.size; ++i)
      if (this.heap[i] === d) {
        this.bubbleUp(i);
        break;
      }
  }
};
var OPTICS = {
  run: (data, minPts, epsilon, getterFn, distFn) => {
    const getter = getterFn;
    const dist = distFn;
    const dists = {};
    const result = [];
    const points = [];
    data.forEach((d, idx) => {
      points.push(new OpticsPoint(idx, d, getter(d)));
    });
    points.forEach((point1, index1) => {
      points.forEach((point2, index2) => {
        const d = dist(point1.vector, point2.vector);
        if (!dists[index1])
          dists[index1] = {};
        dists[index1][index2] = d;
      });
    });
    function getNeighbors(centerPoint) {
      const neighbors = [];
      points.forEach((point) => {
        if (dists[centerPoint.index][point.index] < epsilon && !point.isProcessed)
          neighbors.push(point);
      });
      return neighbors.sort(
        (a, b) => dists[centerPoint.index][a.index] - dists[centerPoint.index][b.index]
      );
    }
    function setCoreDistance(mutablePoint, neighbors) {
      const _point = mutablePoint;
      if (neighbors.length <= minPts - 1)
        _point.coreDistance = null;
      else {
        _point.coreDistance = dists[mutablePoint.index][neighbors[minPts - 1].index];
      }
    }
    points.forEach((centerPoint) => {
      const _point = centerPoint;
      if (_point.isProcessed)
        return;
      _point.isProcessed = true;
      _point.reachability = epsilon + 1;
      let neighbors = getNeighbors(_point);
      const heap = new Heap();
      const update = (_neighbors, _centerPoint) => {
        const { coreDistance } = _centerPoint;
        _neighbors.forEach((p) => {
          const _p = p;
          const r = Math.max(
            coreDistance,
            dists[_centerPoint.index][_p.index]
          );
          if (_p.reachability < 0) {
            _p.reachability = r;
            heap.insert(_p);
          } else if (_p.reachability > r) {
            _p.reachability = r;
            heap.update(_p);
          }
        });
      };
      setCoreDistance(centerPoint, neighbors);
      result.push(centerPoint);
      if (centerPoint.coreDistance) {
        update(neighbors, centerPoint);
        while (!heap.isEmpty()) {
          const current = heap.get();
          if (current) {
            current.isProcessed = true;
            neighbors = getNeighbors(current);
            setCoreDistance(current, neighbors);
            result.push(current);
            if (current.coreDistance)
              update(neighbors, current);
          }
        }
      }
    });
    return result;
  },
  extractClusters: (resultPoints, epsilon) => {
    if (!resultPoints.length) {
      return [];
    }
    const clusters = [];
    let currCluster = [resultPoints[0]];
    for (let i = 1; i < resultPoints.length; i++) {
      const point = resultPoints[i];
      if (point.reachability > epsilon) {
        clusters.push(currCluster);
        currCluster = [point];
      } else {
        currCluster.push(point);
      }
    }
    if (currCluster.length) {
      clusters.push(currCluster);
    }
    return clusters;
  }
};

// src/selectors/cluster.ts
var EPSILON = 1e3;
var MIN_ITEMS_IN_CLUSTER = 1;
function getClusterBounds(cluster, itemBoundsMap) {
  const clusterBoundsArray = cluster.map(
    (item) => itemBoundsMap.get(item.datum)
  );
  return RectangleUtils2.uniteAll(clusterBoundsArray);
}
function multipleMaxBy(arr, maxByFn) {
  if (!arr.length) {
    return [];
  }
  const itemsGroupedByFn = groupBy(arr, maxByFn);
  const keys = Object.keys(itemsGroupedByFn).map((k) => +k);
  const maxKey = Math.max(...keys);
  return itemsGroupedByFn[maxKey];
}
var largestCluster = action((design) => {
  const externalItems = selectors.getExternalItemIds(design);
  const itemBoundsMap = /* @__PURE__ */ new Map();
  externalItems.forEach((itemId) => {
    const bounds2 = selectors.getBounds(design, itemId, true);
    if (bounds2) {
      itemBoundsMap.set(itemId, bounds2);
    }
  });
  const resultPoints = OPTICS.run(
    externalItems,
    MIN_ITEMS_IN_CLUSTER,
    EPSILON,
    (item) => {
      const itemBounds = itemBoundsMap.get(item);
      return RectangleUtils2.getCenter(itemBounds);
    },
    PointUtils2.distance
  );
  const clusters = OPTICS.extractClusters(resultPoints, EPSILON);
  if (!clusters.length) {
    return null;
  }
  const maxClusters = multipleMaxBy(clusters, (cluster) => cluster.length);
  if (maxClusters.length === 1) {
    return getClusterBounds(maxClusters[0], itemBoundsMap);
  }
  const largestClusterPoints = maxBy(maxClusters, (cluster) => {
    const [width, height] = getClusterBounds(cluster, itemBoundsMap).size;
    return width * height;
  });
  return getClusterBounds(largestClusterPoints, itemBoundsMap);
});

// src/selectors/view.ts
import {
  ItemUtils,
  MatrixUtils as MatrixUtils2,
  RectangleUtils as RectangleUtils3,
  selectors as selectors2
} from "@ballpark/design-creator";
function bounds(view) {
  return RectangleUtils3.transform(
    RectangleUtils3.create([0, 0], view.size),
    MatrixUtils2.invert(view.matrix)
  );
}
function rawBounds(view) {
  return RectangleUtils3.transform(
    RectangleUtils3.create([0, 0], view.size),
    MatrixUtils2.invert(view.rawMatrix)
  );
}
function center(view) {
  return RectangleUtils3.getCenter(view.bounds);
}
function rawCenter(view) {
  return RectangleUtils3.getCenter(view.rawBounds);
}
function canZoomIn(view) {
  return ZOOM_FACTORS[ZOOM_FACTORS.length - 1] > view.zoomFactor;
}
function canZoomOut(view) {
  return ZOOM_FACTORS[0] < view.zoomFactor;
}
function zoomLevel(view) {
  const currentZoomFactor = view.zoomFactor;
  let level = 0;
  for (let i = 0; i < ZOOM_FACTORS.length; i++) {
    if (Math.abs(ZOOM_FACTORS[i] - currentZoomFactor) < Math.abs(ZOOM_FACTORS[level] - currentZoomFactor)) {
      level = i;
    }
  }
  return level;
}
function offset(view) {
  return MatrixUtils2.getTranslation(view.matrix);
}
function rawOffset(view) {
  return MatrixUtils2.getTranslation(view.rawMatrix);
}
function zoomFactor(view) {
  return +MatrixUtils2.getScaling(view.matrix)[0];
}
function isEmpty(view, design) {
  const isEmptyDesign = selectors2.getChildren(design).length === 0;
  if (isEmptyDesign)
    return true;
  const walker = selectors2.walk(design);
  let step = walker.next();
  while (!step.done) {
    const [item] = step.value;
    const itemBounds = selectors2.getBounds(design, item.id, true);
    const isVisible = itemBounds && RectangleUtils3.intersects(itemBounds, view.bounds);
    const isExternal = !ItemUtils.isParent(item) || !ItemUtils.getChildIds(item).length;
    if (isVisible && isExternal) {
      return false;
    }
    step = walker.next(isVisible);
  }
  return true;
}

// src/View.ts
var View = class {
  constructor({
    design,
    matrix = MatrixUtils3.create(),
    size = SizeUtils.create(),
    gridSize = 1,
    scaleGrid = false
  }) {
    this.following = null;
    this.gridSize = 1;
    this.scaleGrid = false;
    this.pan = (offset2, options = {}) => {
      return pan(this, offset2, options);
    };
    this.panToPoint = (offset2, options = {}) => {
      return panToPoint(this, offset2, options);
    };
    this.panToArtboardCenter = () => panToArtboardCenter(this, this._design);
    this.panToCluster = () => panToCluster(this, this._design);
    this.panAndZoomToFitArtboard = () => panAndZoomToFitArtboard(this, this._design);
    this.panAndZoomToFitCluster = (options = {}) => panAndZoomToFitCluster(this, this._design, options);
    this.setZoomFactor = (zoomFactor2, options = {}) => setZoomFactor(this, zoomFactor2, options);
    this.zoomToFitBounds = (bounds2) => zoomToFitBounds(this, bounds2);
    this.zoomToFitArtboard = () => zoomToFitArtboard(this, this._design);
    this.zoomToFitCluster = () => zoomToFitCluster(this, this._design);
    this.zoomIn = (options = {}) => zoomIn(this, options);
    this.zoomOut = (options = {}) => zoomOut(this, options);
    this._design = design;
    this._matrix = matrix;
    this.size = size;
    this.gridSize = gridSize;
    this.scaleGrid = scaleGrid;
    makeAutoObservable(this, {
      _design: false,
      bounds: computed.struct,
      center: computed.struct,
      offset: computed.struct,
      largestCluster: computed.struct
    });
  }
  set design(design) {
    this._design = design;
  }
  set matrix(matrix) {
    if (!MatrixUtils3.isInvertible(matrix))
      return;
    this._matrix = matrix;
  }
  get matrix() {
    if (this.gridSize === void 0) {
      return this._matrix;
    }
    const topLeft = PointUtils3.transform([0, 0], this._matrix);
    const scale = MatrixUtils3.getScaling(this._matrix)[0];
    const targetTopLeft = PointUtils3.roundToNearest(
      topLeft,
      this.scaleGrid ? this.gridSize * scale : this.gridSize
    );
    const delta = PointUtils3.divide(
      PointUtils3.subtract(targetTopLeft, topLeft),
      scale
    );
    return MatrixUtils3.translate(this._matrix, delta);
  }
  /**
   * The transformation matrix of the view without snapping to the grid
   */
  get rawMatrix() {
    return this._matrix;
  }
  get bounds() {
    return bounds(this);
  }
  /**
   * The bounds in the design coordinate space that the view covers without snapping to the grid
   */
  get rawBounds() {
    return rawBounds(this);
  }
  get canZoomIn() {
    return canZoomIn(this);
  }
  get canZoomOut() {
    return canZoomOut(this);
  }
  get center() {
    return center(this);
  }
  /**
   * The center of the view in the design coordinate space without snapping to the grid
   */
  get rawCenter() {
    return rawCenter(this);
  }
  get isEmpty() {
    return isEmpty(this, this._design);
  }
  get largestCluster() {
    return largestCluster(this._design);
  }
  get offset() {
    return offset(this);
  }
  /**
   * The view offset without snapping to the grid
   */
  get rawOffset() {
    return rawOffset(this);
  }
  get zoomLevel() {
    return zoomLevel(this);
  }
  get zoomFactor() {
    return Math.max(zoomFactor(this), NumberUtils2.EPSILON);
  }
};

// src/react.tsx
import { Fragment as Fragment2, jsx } from "react/jsx-runtime";
var ViewContext = createContext(null);
function useView() {
  const state = useContext(ViewContext);
  if (state === null)
    throw new Error("useView() called outside of ViewProvider");
  return state;
}
function useWeakView(options) {
  const maybeView = useContext(ViewContext);
  const { design } = useDesign();
  const view = useMemoOne(
    () => maybeView != null ? maybeView : new View(__spreadProps(__spreadValues({}, options), { design })),
    [maybeView, design, options == null ? void 0 : options.matrix, options == null ? void 0 : options.size]
  );
  return view;
}
function ViewProvider({
  children,
  options = {}
}) {
  const { design } = useDesign();
  const [view] = useState(() => new View(__spreadProps(__spreadValues({}, options), { design })));
  const { matrix, size, gridSize, scaleGrid } = options;
  usePropReaction(
    (props) => {
      Object.assign(view, props);
    },
    {
      matrix,
      size,
      gridSize,
      scaleGrid
    }
  );
  return /* @__PURE__ */ jsx(ViewContext.Provider, { value: view, children });
}
function WeakViewProvider({ children }) {
  const state = useContext(ViewContext);
  if (state === null) {
    return /* @__PURE__ */ jsx(ViewProvider, { children });
  }
  return /* @__PURE__ */ jsx(Fragment2, { children });
}

// src/components/DragToPan.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var DragToPan = observer(function DragToPan2({
  modifier = SpecialModifierKey.Space,
  children
}) {
  const view = useView();
  const [isPanEnabled, setIsPanEnabled] = useState2(modifier === null);
  useEffect(() => {
    if (modifier === null) {
      setIsPanEnabled(true);
    } else {
      setIsPanEnabled(false);
    }
  }, [modifier]);
  useModifierKey(modifier, setIsPanEnabled);
  const onPointerDown = useCallback((e) => {
    e.stopPropagation();
  }, []);
  const onDrag = useTransactionCallback(
    (_, data) => {
      const { deltaX, deltaY } = data;
      const amount = PointUtils4.create(deltaX, deltaY);
      view.pan(amount);
    },
    [view]
  );
  return /* @__PURE__ */ jsx2(
    Draggable,
    {
      "data-testid": "panning-controls",
      disabled: !isPanEnabled,
      onDrag,
      onPointerDown,
      style: {
        // important to not remove the layer when pan isn't enabled because otherwise the browser
        // won't change the cursor back to auto until it moves again
        cursor: isPanEnabled ? "grab" : "auto",
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: isPanEnabled ? "all" : "none",
        flex: 1
      },
      children
    }
  );
});

// src/components/ScrollToPanAndZoom.tsx
import { MatrixUtils as MatrixUtils4, PointUtils as PointUtils5 } from "@ballpark/design-creator";
import { debounce } from "lodash-es";
import { observer as observer2 } from "mobx-react";
import { useCallback as useCallback2, useLayoutEffect, useRef } from "react";
var isSSR = typeof window === "undefined";
var SCROLL_FACTOR = !isSSR && navigator.userAgent.toLowerCase().indexOf("firefox") > -1 ? 0.5 : 1;
var ScrollToPanAndZoom = observer2(function ScrollToPanAndZoom2({
  timeout = 500,
  sceneEl,
  onPanZoomStart,
  onPanZoomEnd,
  pan: pan2 = true,
  zoom = true
}) {
  const view = useWeakView();
  const timerRef = useRef(null);
  const getClientOffset = useCallback2(
    debounce(
      () => {
        if (!sceneEl)
          return [0, 0];
        const bounds2 = sceneEl.getBoundingClientRect();
        return [bounds2.left, bounds2.top];
      },
      timeout,
      { leading: true, trailing: false }
    ),
    [sceneEl]
  );
  useLayoutEffect(() => {
    if (!sceneEl)
      return;
    function panAndZoom(event) {
      if (event.ctrlKey && zoom || pan2) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (onPanZoomStart) {
        onPanZoomStart();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (onPanZoomEnd) {
        timerRef.current = setTimeout(onPanZoomEnd, timeout);
      }
      if (event.ctrlKey && zoom) {
        const zoomDelta = -event.deltaY * 5e-3;
        const delta = 1 + zoomDelta;
        const sceneOffset = getClientOffset();
        const offsetX = event.clientX - sceneOffset[0];
        const offsetY = event.clientY - sceneOffset[1];
        const center2 = PointUtils5.transform(
          [offsetX, offsetY],
          MatrixUtils4.invert(view.matrix)
        );
        view.setZoomFactor(view.zoomFactor * delta, { center: center2 });
      } else if (pan2) {
        const x = -event.deltaX * SCROLL_FACTOR;
        const y = -event.deltaY * SCROLL_FACTOR;
        view.pan([x, y]);
      }
    }
    sceneEl.addEventListener("wheel", panAndZoom, true);
    return () => {
      sceneEl.removeEventListener("wheel", panAndZoom, true);
    };
  }, [
    timeout,
    sceneEl,
    onPanZoomStart,
    onPanZoomEnd,
    view,
    getClientOffset,
    zoom,
    pan2
  ]);
  return null;
});

// src/components/Scene.tsx
import {
  OverlayPortalProvider,
  SpecialModifierKey as SpecialModifierKey2
} from "@ballpark/design-core";
import { useDesign as useDesign2 } from "@ballpark/design-plugin-react";
import { useOfflineValue } from "@ballpark/realtime-plugin-offline";
import { isEmpty as isEmpty2 } from "lodash-es";
import { runInAction } from "mobx";
import { observer as observer3 } from "mobx-react";
import {
  useEffect as useEffect2,
  useLayoutEffect as useLayoutEffect2,
  useRef as useRef2,
  useState as useState3
} from "react";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var BaseScene = observer3(function BaseScene2({
  scrollToPanAndZoom = true,
  autoPosition = true,
  dragToPan = true,
  dragToPanModifier = SpecialModifierKey2.Space,
  forgetLastView = false,
  noViewReset = false,
  onViewInitialized,
  constrainZoomToFit = true,
  zoomToFitPadding = 0,
  children,
  width = "100%",
  height = "100%"
}) {
  const { design } = useDesign2();
  const overlayContainer = useRef2(null);
  const [viewInitialized, setViewInitialized] = useState3(false);
  const [sceneEl, setSceneEl] = useState3(null);
  const view = useView();
  const viewSized = useUpdateViewBoundsOnContainerResize(sceneEl);
  const { value: previousView, ready: previousViewReady } = useOfflineValue(
    "view",
    () => {
      const { zoomFactor: zoom, center: center2 } = view;
      return { zoom, center: center2 };
    },
    [view],
    forgetLastView
  );
  useEffect2(() => {
    if (noViewReset && viewInitialized)
      return;
    if (!previousViewReady)
      return;
    if (!viewSized)
      return;
    if (isEmpty2(design))
      return;
    runInAction(() => {
      if (autoPosition) {
        if (previousView) {
          view.panToPoint(previousView.center);
          view.setZoomFactor(previousView.zoom);
        } else {
          view.panAndZoomToFitCluster({
            constrainToZoomScale: constrainZoomToFit,
            padding: zoomToFitPadding
          });
        }
      }
      onViewInitialized == null ? void 0 : onViewInitialized();
    });
    setViewInitialized(true);
  }, [
    autoPosition,
    constrainZoomToFit,
    design,
    noViewReset,
    onViewInitialized,
    previousView,
    previousViewReady,
    sceneEl,
    view,
    viewInitialized,
    viewSized,
    zoomToFitPadding
  ]);
  return /* @__PURE__ */ jsx3(OverlayPortalProvider, { container: overlayContainer.current, children: /* @__PURE__ */ jsxs(
    "div",
    {
      "data-testid": "scene",
      ref: setSceneEl,
      style: { width, height, position: "relative" },
      children: [
        scrollToPanAndZoom && /* @__PURE__ */ jsx3(ScrollToPanAndZoom, { sceneEl }),
        viewInitialized && children,
        dragToPan && /* @__PURE__ */ jsx3(DragToPan, { modifier: dragToPanModifier }),
        /* @__PURE__ */ jsx3("div", { ref: overlayContainer })
      ]
    }
  ) });
});
var Scene = (props) => {
  return /* @__PURE__ */ jsx3(WeakViewProvider, { children: /* @__PURE__ */ jsx3(BaseScene, __spreadValues({}, props)) });
};
function useUpdateViewBoundsOnContainerResize(containerEl) {
  const view = useView();
  const [viewReady, setViewReady] = useState3(false);
  useLayoutEffect2(() => {
    if (!containerEl)
      return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length)
        return;
      const { inlineSize: width, blockSize: height } = entries[0].borderBoxSize[0];
      runInAction(() => {
        view.size = [width, height];
        setViewReady(true);
      });
    });
    resizeObserver.observe(containerEl);
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerEl, view]);
  return viewReady;
}
export {
  DragToPan,
  Scene,
  ScrollToPanAndZoom,
  View,
  ViewProvider,
  WeakViewProvider,
  useView,
  useWeakView
};
